import { t, localizedText } from '@/utils/i18n'
import { Button } from '@/components/ui/Button'

export function Footer(): HTMLElement {
  const footer = document.createElement('footer')
  footer.className = 'bg-slate-900 text-white pt-24 pb-12 relative overflow-hidden'

  // Mesh gradient background effect
  const mesh = document.createElement('div')
  mesh.className = 'absolute inset-0 opacity-20 pointer-events-none'
  mesh.style.backgroundImage = 'radial-gradient(at 0% 100%, rgba(239, 87, 48, 0.3) 0px, transparent 50%), radial-gradient(at 100% 0%, rgba(15, 23, 42, 0.3) 0px, transparent 50%)'
  footer.appendChild(mesh)

  const container = document.createElement('div')
  container.className = 'container-custom relative z-10'

  const grid = document.createElement('div')
  grid.className = 'grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16'

  // Column 1: Company Info (Span 4)
  const col1 = document.createElement('div')
  col1.className = 'md:col-span-4 space-y-8'

  const logo = document.createElement('div')
  logo.className = 'flex items-center'

  const logoImg = document.createElement('img')
  logoImg.src = '/logo/GIGAette_logo_white.png'
  logoImg.alt = 'GIGAette'
  logoImg.className = 'h-8'

  logo.appendChild(logoImg)

  const description = document.createElement('p')
  description.textContent = t('footer.companyDescription')
  description.className = 'text-slate-400 text-sm leading-relaxed max-w-sm'

  col1.appendChild(logo)
  col1.appendChild(description)

  // Column 2: Quick Links (Span 2)
  const col2 = document.createElement('div')
  col2.className = 'md:col-span-2'
  const col2Title = document.createElement('h3')
  col2Title.textContent = t('footer.quickLinks')
  col2Title.className = 'font-bold text-base mb-6 text-white tracking-wide'

  const quickLinks = [
    { label: t('nav.technology'), href: '/technology' },
    { label: t('nav.industries'), href: '/applications' },
    { label: t('nav.projects'), href: '/projects' },
  ]

  const linksList = document.createElement('ul')
  linksList.className = 'space-y-4'

  quickLinks.forEach(link => {
    const li = document.createElement('li')
    const a = document.createElement('a')
    a.href = link.href
    a.textContent = link.label
    a.className = 'text-slate-400 hover:text-orange-400 transition-colors duration-200 text-sm font-medium'
    a.addEventListener('click', (e) => {
      e.preventDefault()
      window.dispatchEvent(new CustomEvent('navigate', { detail: link.href }))
    })
    li.appendChild(a)
    linksList.appendChild(li)
  })

  col2.appendChild(col2Title)
  col2.appendChild(linksList)

  // Column 3: Company (Span 2)
  const col3 = document.createElement('div')
  col3.className = 'md:col-span-2'
  const col3Title = document.createElement('h3')
  col3Title.textContent = t('nav.company')
  col3Title.className = 'font-bold text-base mb-6 text-white tracking-wide'

  const companyLinks = [
    { label: t('nav.company'), href: '/company' },
    { label: t('nav.about'), href: '/company/about' },
    { label: t('nav.partners'), href: '/company/partners' },
    { label: t('nav.news'), href: '/company/news' },
    { label: t('nav.ir'), href: '/ir' },
    { label: t('nav.contact'), href: '/contact' },
  ]

  const companyList = document.createElement('ul')
  companyList.className = 'space-y-4'

  companyLinks.forEach(link => {
    const li = document.createElement('li')
    const a = document.createElement('a')
    a.href = link.href
    a.textContent = link.label
    a.className = 'text-slate-400 hover:text-orange-400 transition-colors duration-200 text-sm font-medium'
    a.addEventListener('click', (e) => {
      e.preventDefault()
      window.dispatchEvent(new CustomEvent('navigate', { detail: link.href }))
    })
    li.appendChild(a)
    companyList.appendChild(li)
  })

  col3.appendChild(col3Title)
  col3.appendChild(companyList)

  // Column 4: Newsletter (Span 4)
  const col4 = document.createElement('div')
  col4.className = 'md:col-span-4'
  const col4Title = document.createElement('h3')
  col4Title.textContent = t('footer.newsletter.headline')
  col4Title.className = 'font-bold text-base mb-6 text-white tracking-wide'

  const form = document.createElement('form')
  form.className = 'space-y-4'

  const input = document.createElement('input')
  input.type = 'email'
  input.placeholder = t('footer.newsletter.placeholder')
  input.required = true
  input.className = 'w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-orange-500/50 text-white placeholder-slate-500 text-sm transition-all duration-300 hover:bg-white/10'

  const submitBtn = Button({
    children: t('footer.newsletter.button'),
    variant: 'primary',
    size: 'sm',
    type: 'submit',
    className: 'w-full font-semibold',
  })

  form.addEventListener('submit', (e) => {
    e.preventDefault()
    console.log('Newsletter subscription:', input.value)
    alert(t('contactForm.successMessage'))
    input.value = ''
  })

  form.appendChild(input)
  form.appendChild(submitBtn)

  col4.appendChild(col4Title)
  col4.appendChild(form)

  // Add all columns to grid
  grid.appendChild(col1)
  grid.appendChild(col2)
  grid.appendChild(col3)
  grid.appendChild(col4)

  // Copyright
  const copyright = document.createElement('div')
  copyright.className = 'mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-slate-500 text-sm'

  const copyrightText = document.createElement('p')
  copyrightText.textContent = t('footer.copyright')

  const socialLinks = document.createElement('div')
  socialLinks.className = 'flex items-center space-x-6'

  // Privacy & Cookies link
  const privacyLink = document.createElement('a')
  privacyLink.href = '/privacy'
  privacyLink.textContent = localizedText({ en: 'Privacy & Cookies', ko: '개인정보 및 쿠키' })
  privacyLink.className = 'text-slate-500 hover:text-orange-400 transition-colors duration-200 text-sm font-medium'
  privacyLink.addEventListener('click', (e) => {
    e.preventDefault()
    window.dispatchEvent(new CustomEvent('navigate', { detail: '/privacy' }))
  })
  socialLinks.appendChild(privacyLink)

  copyright.appendChild(copyrightText)
  copyright.appendChild(socialLinks)

  container.appendChild(grid)
  container.appendChild(copyright)
  footer.appendChild(container)

  return footer
}
